import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Search, ShieldCheck, Users, Home, ArrowRight, CheckCircle } from 'lucide-react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { Link } from 'react-router-dom';
export function HowItWorks() {
  const ref = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const steps = [{
    number: '01',
    icon: Search,
    title: 'Browse Verified Hostels',
    description: 'Search through 850+ physically verified properties near your campus. Every listing includes real photos and honest reviews.',
    color: 'from-blue-500 to-cyan-600',
    image: 'https://cdn.magicpatterns.com/uploads/goHkoTBNqivNWfS6sUUgPa/Copy_of_Billvast_Shirt_Mock-up_.png'
  }, {
    number: '02',
    icon: Users,
    title: 'Match with Roommates',
    description: 'Take our 2-minute Vibe Check quiz. Our AI pairs you with compatible roommates based on lifestyle and preferences.',
    color: 'from-brand-primary to-purple-600',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  }, {
    number: '03',
    icon: ShieldCheck,
    title: 'Verify & Pay Securely',
    description: 'Visit the property, verify everything matches the listing, then pay through our secure escrow system. Your money is protected.',
    color: 'from-emerald-500 to-green-600',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  }, {
    number: '04',
    icon: Home,
    title: 'Move In with Confidence',
    description: "Once verified, move into your new home knowing it's safe, legitimate, and exactly as advertised. Welcome home!",
    color: 'from-orange-500 to-red-600',
    image: 'https://images.unsplash.com/photo-1556912173-46c336c7fd55?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  }];
  return <Section ref={ref} className="bg-gradient-to-b from-slate-50 to-white relative overflow-hidden" data-id="element-204" data-mp="3tzvsh55eyle3">
      {/* Animated Background */}
      <motion.div style={{
      y
    }} className="absolute top-0 right-0 w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl" data-id="element-205" data-mp="2rhnrm6bvszk" />

      <div className="relative z-10" data-id="element-206" data-mp="cqqqb3f9efxj">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-207" data-mp="cpp1zx98fwxv">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/10 rounded-full text-brand-primary text-sm font-medium mb-6" data-id="element-208" data-mp="vjktz17fmlmg">
            <CheckCircle className="w-4 h-4" data-id="element-209" data-mp="15k3p54ior349" />
            <span data-id="element-210" data-mp="10mbxmblydj0l">Simple Process</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4" data-id="element-211" data-mp="10ppzzjum9s81">
            How It <span className="text-brand-primary" data-id="element-212" data-mp="vqd469lrpatb">Works</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto" data-id="element-213" data-mp="ro9p7azw3o3a">
            From search to move-in, we've made finding safe student housing
            simple and stress-free.
          </p>
        </motion.div>

        <div className="space-y-24" data-id="element-214" data-mp="1jemtvmm3qipq">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`} data-id="element-215" data-mp="1eltoqxmq18d4" data-mpi={index}>
              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`} data-id="element-216" data-mp="1dw440b7oocmy">
                <motion.div initial={{
              scale: 0
            }} whileInView={{
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.2,
              type: 'spring'
            }} className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} text-white mb-6 shadow-lg`} data-id="element-217" data-mp="1hw4r0k8ulw5m">
                  <step.icon className="w-8 h-8" data-id="element-218" data-mp="2tbtdlai6wt5w" />
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.3
            }} className="mb-4" data-id="element-219" data-mp="hvapdp28auy1">
                  <span className={`text-6xl font-bold bg-gradient-to-br ${step.color} bg-clip-text text-transparent opacity-20`} data-id="element-220" data-mp="3q419d0apu1fb">
                    {step.number}
                  </span>
                </motion.div>

                <motion.h3 initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.4
            }} className="text-3xl font-bold text-brand-dark mb-4" data-id="element-221" data-mp="3ye2tzt57zmy">
                  {step.title}
                </motion.h3>

                <motion.p initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.5
            }} className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-222" data-mp="219b30lkrrhpa">
                  {step.description}
                </motion.p>

                <motion.div initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + 0.6
            }} data-id="element-223" data-mp="336rukj3cpc9s">
                  <Link to={index === 0 ? '/find-hostel' : index === 1 ? '/roommate-match' : '/signup'} data-id="element-224" data-mp="2ka0h4qahprdn">
                    <Button variant="outline" className="group" data-id="element-225" data-mp="26ic7qurmlh0d">
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-226" data-mp="21gmst41a8j70" />
                    </Button>
                  </Link>
                </motion.div>
              </div>

              {/* Image */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1 + 0.3,
            duration: 0.6
          }} className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`} data-id="element-227" data-mp="3pe47a61swsdm">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-228" data-mp="2sowkkupg4d84">
                  <motion.img src={step.image} alt={step.title} className="w-full h-96 object-cover" whileHover={{
                scale: 1.05
              }} transition={{
                duration: 0.4
              }} data-id="element-229" data-mp="2xm52cu6omf8u" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-20`} data-id="element-230" data-mp="201d8rz50zexs" />

                  {/* Floating Badge */}
                  <motion.div initial={{
                y: 20,
                opacity: 0
              }} whileInView={{
                y: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1 + 0.7
              }} className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg" data-id="element-231" data-mp="2dz1u18308o3d">
                    <div className="flex items-center gap-3" data-id="element-232" data-mp="36npljleck9pd">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center`} data-id="element-233" data-mp="31rs399jiwt9y">
                        <step.icon className="w-5 h-5 text-white" data-id="element-234" data-mp="um4l2jfy0z6b" />
                      </div>
                      <div data-id="element-235" data-mp="pn1rpw9bshe6">
                        <div className="font-bold text-brand-dark" data-id="element-236" data-mp="pocvvx6onr5t">
                          Step {step.number}
                        </div>
                        <div className="text-sm text-slate-600" data-id="element-237" data-mp="lk557ugo014y">
                          {step.title}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Decorative Elements */}
                <motion.div animate={{
              y: [0, -10, 0],
              rotate: [0, 5, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className={`absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br ${step.color} opacity-20 blur-xl`} data-id="element-238" data-mp="199pram847eig" />
                <motion.div animate={{
              y: [0, 10, 0],
              rotate: [0, -5, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} className={`absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-gradient-to-br ${step.color} opacity-20 blur-xl`} data-id="element-239" data-mp="29ksczvtk7ejf" />
              </motion.div>
            </motion.div>)}
        </div>

        {/* CTA Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-24 text-center" data-id="element-240" data-mp="319vjzjctz2zr">
          <div className="bg-gradient-to-br from-brand-dark to-brand-dark-secondary rounded-3xl p-12 relative overflow-hidden" data-id="element-241" data-mp="25987n3srsmp8">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle, #b583c9 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} data-id="element-242" data-mp="2j999omkru06a"></div>
            <div className="relative z-10" data-id="element-243" data-mp="2o8ceq70r46e9">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-244" data-mp="2o5ghym0n3nre">
                Ready to Find Your Perfect Home?
              </h3>
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto" data-id="element-245" data-mp="2xwueckxao7gb">
                Join 5,000+ students who've found safe, verified housing through
                CampusAparte.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-246" data-mp="36ytu79wkp5ho">
                <Link to="/find-hostel" data-id="element-247" data-mp="3qk3ubc9zo4d1">
                  <Button size="lg" className="bg-brand-primary hover:bg-brand-light text-white" data-id="element-248" data-mp="3ql5cs1tc3t4s">
                    Browse Hostels
                    <ArrowRight className="ml-2 w-5 h-5" data-id="element-249" data-mp="3hie05yoq2rbk" />
                  </Button>
                </Link>
                <Link to="/roommate-match" data-id="element-250" data-mp="3lj6aywd8t49y">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-id="element-251" data-mp="37tco2jlasu2v">
                    Find Roommates
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>;
}